import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  CardGiftcard as GiftIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../../theme";

interface ResourceLink {
  id: string;
  title: string;
  description: string;
  icon: "download" | "gift";
  url: string;
}

interface ThankYouModalProps {
  open: boolean;
  onClose: () => void;
  resources?: ResourceLink[];
  showCheckIcon?: boolean;
}

const DEFAULT_RESOURCES: ResourceLink[] = [
  {
    id: "guide",
    title: "Download Free Guide",
    description: "Complete Guide to Funerals in Harare",
    icon: "download",
    url: "#",
  },
  {
    id: "voucher",
    title: "Get $50 Discount Voucher",
    description: "For Funeral Programs Printing",
    icon: "gift",
    url: "#",
  },
];

const ThankYouModal: React.FC<ThankYouModalProps> = ({
  open,
  onClose,
  resources = DEFAULT_RESOURCES,
  showCheckIcon = true,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleResourceClick = (url: string) => {
    if (url && url !== "#") {
      window.open(url, "_blank");
    }
  };

  const getIcon = (iconType: "download" | "gift") => {
    return iconType === "download" ? DownloadIcon : GiftIcon;
  };

  const padding = isMobile ? 2 : 3;
  const iconSize = isMobile ? 48 : 60;
  const titleSize = isMobile ? 20 : 24;
  const buttonPadding = isMobile ? 1.5 : 2;
  const gap = isMobile ? 1.5 : 2;
  const mb = isMobile ? 2 : 3;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          margin: isMobile ? 1 : 2,
          maxHeight: isMobile ? "95vh" : "90vh",
        },
      }}
    >
      <Box
        sx={{
          p: padding,
          bgcolor: colors.primary[500],
          color: "white",
          position: "relative",
          textAlign: "center",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: isMobile ? 6 : 8,
            top: isMobile ? 6 : 8,
            color: "white",
            padding: "4px",
          }}
        >
          <CloseIcon />
        </IconButton>

        {showCheckIcon && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: isMobile ? 1.5 : 2,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: iconSize, color: "white" }} />
          </Box>
        )}

        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mb: 1, fontSize: titleSize }}
        >
          Thank You!
        </Typography>
        <Typography
          variant="body2"
          sx={{
            opacity: 0.9,
            fontSize: isMobile ? 13 : 14,
            lineHeight: 1.4,
          }}
        >
          Your quote request has been submitted successfully. We'll send your
          Personalised quote to your WhatsApp shortly.
        </Typography>
      </Box>

      <DialogContent sx={{ p: padding }}>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{
            mb,
            textAlign: "center",
            fontSize: isMobile ? "0.9375rem" : undefined,
          }}
        >
          While you wait, here are some helpful resources
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap, mb }}>
          {resources.map((resource) => {
            const Icon = getIcon(resource.icon);
            return (
              <Button
                key={resource.id}
                onClick={() => handleResourceClick(resource.url)}
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[400]} 100%)`,
                  color: "white",
                  p: buttonPadding,
                  borderRadius: 2,
                  textTransform: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  textAlign: "left",
                  minHeight: isMobile ? 60 : 72,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: isMobile ? 1.5 : 2,
                  }}
                >
                  <Box
                    sx={{
                      width: isMobile ? 40 : 48,
                      height: isMobile ? 40 : 48,
                      borderRadius: 1.5,
                      bgcolor: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: isMobile ? 20 : 24 }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: isMobile ? "0.875rem" : "1rem",
                        mb: isMobile ? 0.25 : 0.5,
                      }}
                    >
                      {resource.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.75rem" : "0.875rem",
                        opacity: 0.9,
                      }}
                    >
                      {resource.description}
                    </Typography>
                  </Box>
                </Box>
                <ArrowForwardIcon
                  sx={{ fontSize: isMobile ? 18 : 20, flexShrink: 0 }}
                />
              </Button>
            );
          })}
        </Box>

        <Box
          sx={{
            bgcolor: "grey.50",
            borderRadius: 2,
            p: isMobile ? 2 : 2.5,
            mb: isMobile ? 2 : 3,
            textAlign: "center",
            border: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              lineHeight: 1.5,
              fontSize: isMobile ? "0.8125rem" : undefined,
            }}
          >
            We appreciate your trust in Muzukuru. Our team will contact you
            shortly via WhatsApp with your Personalised quote.
          </Typography>
        </Box>

        <Button
          fullWidth
          variant="outlined"
          onClick={onClose}
          sx={{
            py: isMobile ? 1.25 : 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            borderColor: "divider",
            "&:hover": {
              borderColor: colors.primary[400],
              bgcolor: "rgba(0,0,0,0.02)",
            },
          }}
        >
          Close
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default ThankYouModal;
